import React, { useState, useEffect } from 'react';
import { 
  X, 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  MessageSquare, 
  Paperclip,
  Send,
  Plus,
  Trash2,
  Upload
} from 'lucide-react';
import { MeetingService, WorkshopForMeeting, MeetingCreateData } from '../../services/meeting';
import SearchableWorkshopDropdown from '../common/SearchableWorkshopDropdown';
import SearchableUserDropdown from '../common/SearchableUserDropdown';
import SearchableZoneDropdown from '../common/SearchableZoneDropdown';
import { User, UserService } from '../../services/user';
import { Zone, ZoneService } from '../../services/zone';

interface CreateMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (meeting: MeetingCreateData) => void;
  editingMeeting?: any; // We'll handle editing later
}

const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingMeeting
}) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    location: '',
    zone: null as Zone | null,
    customZone: '',
    attendees: [] as number[],
    workshops: [] as number[],
    message: '',
    attachments: [] as File[]
  });

  const [workshops, setWorkshops] = useState<WorkshopForMeeting[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load workshops, users, and zones from API
  useEffect(() => {
    if (isOpen) {
      loadWorkshops();
      loadUsers();
      loadZones();
      
      // Also ensure attachments are loaded if editingMeeting is provided
      if (editingMeeting && editingMeeting.attachments) {
        console.log('Modal opened with editingMeeting, loading attachments:', editingMeeting.attachments);
        setExistingAttachments(editingMeeting.attachments);
      }
    }
  }, [isOpen, editingMeeting]);

  const loadWorkshops = async () => {
    try {
      const workshopsData = await MeetingService.getWorkshopsForMeeting();
      // Ensure workshopsData is an array
      setWorkshops(Array.isArray(workshopsData) ? workshopsData : []);
    } catch (error) {
      console.error('Failed to load workshops:', error);
      // Set empty array on error
      setWorkshops([]);
    }
  };

  const loadUsers = async () => {
    try {
      const usersResponse = await UserService.getAllUsers({ is_active: true });
      setUsers(usersResponse);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const loadZones = async () => {
    try {
      const zonesData = await ZoneService.getZones({ is_active: true });
      setZones(zonesData);
    } catch (error) {
      console.error('Error loading zones:', error);
      setZones([]);
    }
  };

  // Pre-fill form when editing or viewing
  useEffect(() => {
    if (editingMeeting) {
      // Convert email addresses back to user IDs for attendees
      const attendeeIds: number[] = [];
      if (editingMeeting.attendees && Array.isArray(editingMeeting.attendees)) {
        editingMeeting.attendees.forEach((email: string) => {
          const user = users.find(u => u.email === email);
          if (user) {
            attendeeIds.push(user.id);
          }
        });
      }

      // Find matching zone for location
      let selectedZone: Zone | null = null;
      let customZone = '';
      if (editingMeeting.location) {
        const matchingZone = zones.find(z => z.name === editingMeeting.location);
        if (matchingZone) {
          selectedZone = matchingZone;
        } else {
          customZone = editingMeeting.location;
        }
      }

      // Handle existing attachments separately - let the attachments effect handle this
      console.log('Main effect - editingMeeting.attachments:', editingMeeting.attachments);

      setFormData({
        title: editingMeeting.title,
        date: editingMeeting.date,
        time: editingMeeting.time,
        duration: editingMeeting.duration,
        location: editingMeeting.location,
        zone: selectedZone,
        customZone: customZone,
        attendees: attendeeIds,
        workshops: editingMeeting.workshops_info ? editingMeeting.workshops_info.map((w: any) => w.id) : [],
        message: editingMeeting.message,
        attachments: [] // New file uploads only
      });
    } else {
      // Reset form for new meeting
      setExistingAttachments([]);
      setFormData({
        title: '',
        date: '',
        time: '',
        duration: 60,
        location: '',
        zone: null,
        customZone: '',
        attendees: [],
        workshops: [],
        message: '',
        attachments: []
      });
    }
    setErrors({});
  }, [editingMeeting, isOpen, users, zones]);

  // Handle initial meeting data loading (for viewing)
  useEffect(() => {
    console.log('editingMeeting changed:', editingMeeting);
    if (editingMeeting) {
      console.log('editingMeeting.attachments:', editingMeeting.attachments);
      if (editingMeeting.attachments && Array.isArray(editingMeeting.attachments) && editingMeeting.attachments.length > 0) {
        console.log('Setting existing attachments:', editingMeeting.attachments);
        setExistingAttachments(editingMeeting.attachments);
      } else if (editingMeeting.id) {
        // If attachments are missing or empty, fetch the full meeting details
        console.log('Attachments missing or empty, fetching full meeting details for ID:', editingMeeting.id);
        fetchFullMeetingDetails(editingMeeting.id);
      } else {
        console.log('No attachments found, clearing existing attachments');
        setExistingAttachments([]);
      }
    } else {
      console.log('No editingMeeting, clearing existing attachments');
      setExistingAttachments([]);
    }
  }, [editingMeeting]);

  const fetchFullMeetingDetails = async (meetingId: number) => {
    console.log('fetchFullMeetingDetails called with ID:', meetingId);
    try {
      const fullMeeting = await MeetingService.getMeeting(meetingId);
      console.log('Fetched full meeting details:', fullMeeting);
      console.log('Full meeting attachments:', fullMeeting.attachments);
      if (fullMeeting.attachments && Array.isArray(fullMeeting.attachments) && fullMeeting.attachments.length > 0) {
        console.log('Setting attachments from full meeting:', fullMeeting.attachments);
        setExistingAttachments(fullMeeting.attachments);
      } else {
        console.log('No attachments in full meeting either');
        setExistingAttachments([]);
      }
    } catch (error) {
      console.error('Error fetching full meeting details:', error);
      setExistingAttachments([]);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleZoneChange = (zone: Zone | null) => {
    setFormData(prev => ({ 
      ...prev, 
      zone: zone,
      location: zone ? zone.name : prev.customZone || ''
    }));
    // Clear error when user selects a zone
    if (errors.location) {
      setErrors(prev => ({ ...prev, location: '' }));
    }
  };

  const handleCustomZoneAdd = (customZoneName: string) => {
    setFormData(prev => ({ 
      ...prev, 
      customZone: customZoneName,
      location: customZoneName,
      zone: null
    }));
    // Clear error when user adds custom zone
    if (errors.location) {
      setErrors(prev => ({ ...prev, location: '' }));
    }
  };



  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setFormData(prev => ({ 
      ...prev, 
      attachments: [...prev.attachments, ...files] 
    }));
  };

  const removeAttachment = (index: number) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, attachments: newAttachments }));
  };

  const removeExistingAttachment = async (index: number) => {
    if (!editingMeeting?.id) {
      console.error('No meeting ID available for deleting attachment');
      return;
    }

    const attachmentToDelete = existingAttachments[index];
    if (!attachmentToDelete?.id) {
      console.error('No attachment ID available for deletion');
      return;
    }

    try {
      console.log('Deleting attachment:', attachmentToDelete.original_filename, 'ID:', attachmentToDelete.id);
      await MeetingService.deleteAttachment(editingMeeting.id, attachmentToDelete.id);
      
      // Remove from local state after successful deletion
      const newExistingAttachments = existingAttachments.filter((_, i) => i !== index);
      setExistingAttachments(newExistingAttachments);
      
      console.log('Attachment deleted successfully');
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      // You could show a toast notification here
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Meeting title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.time) {
      newErrors.time = 'Time is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    // Validate attendees
    if (formData.attendees.length === 0) {
      newErrors.attendees = 'At least one attendee is required';
    }

    if (formData.workshops.length === 0) {
      newErrors.workshops = 'Please select at least one workshop';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Filter out empty attendees (attendees are now user IDs)
      const validAttendees = formData.attendees.filter(id => id && id > 0);
      
      const meetingData: MeetingCreateData = {
        title: formData.title,
        date: formData.date,
        time: formData.time,
        duration: formData.duration,
        location: formData.location,
        attendees: validAttendees,
        workshops: formData.workshops,
        message: formData.message,
        attachments: formData.attachments
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSubmit(meetingData);
    } catch (error) {
      console.error('Error creating meeting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingMeeting ? 'Edit Meeting' : 'Schedule New Meeting'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Meeting Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter meeting title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-1" />
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600">{errors.date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Time *
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.time ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.time && (
                <p className="mt-1 text-sm text-red-600">{errors.time}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (minutes)
              </label>
              <select
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
          </div>

          {/* Location/Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin size={16} className="inline mr-1" />
              Location/Zone *
            </label>
            <SearchableZoneDropdown
              zones={zones}
              selectedZone={formData.zone}
              onZoneChange={handleZoneChange}
              onCustomZoneAdd={handleCustomZoneAdd}
              placeholder="Search and select a zone or add custom location..."
              className={errors.location ? 'border-red-500' : ''}
            />
            {errors.location && (
              <p className="mt-1 text-sm text-red-600">{errors.location}</p>
            )}
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users size={16} className="inline mr-1" />
              Attendees *
            </label>
            <SearchableUserDropdown
              users={users}
              selectedUsers={formData.attendees}
              onSelectionChange={(selectedIds) => handleInputChange('attendees', selectedIds)}
              placeholder="Search and select attendees..."
              multiple={true}
              className={errors.attendees ? 'border-red-500' : ''}
            />
            {errors.attendees && (
              <p className="mt-1 text-sm text-red-600">{errors.attendees}</p>
            )}
          </div>

          {/* Workshop Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Related Workshops *
            </label>
            <SearchableWorkshopDropdown
              workshops={workshops}
              selectedWorkshops={formData.workshops}
              onSelectionChange={(selectedIds) => handleInputChange('workshops', selectedIds)}
              placeholder="Search and select workshops..."
              multiple={true}
              className={errors.workshops ? 'border-red-500' : ''}
            />
            {errors.workshops && (
              <p className="mt-1 text-sm text-red-600">{errors.workshops}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare size={16} className="inline mr-1" />
              Meeting Message
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add agenda, notes, or additional information for attendees..."
            />
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Paperclip size={16} className="inline mr-1" />
              Attachments
            </label>
            <div className="space-y-2">
              {/* File Upload */}
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-4 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, Images (MAX. 10MB each)
                    </p>
                  </div>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                  />
                </label>
              </div>

              {/* Attached Files List */}
              {(() => {
                console.log('Rendering attachments section:');
                console.log('formData.attachments:', formData.attachments);
                console.log('existingAttachments:', existingAttachments);
                console.log('Should show attachments:', (formData.attachments.length > 0 || existingAttachments.length > 0));
                return null;
              })()}
              {(formData.attachments.length > 0 || existingAttachments.length > 0) && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Attached Files:</h4>
                  
                  {/* Existing attachments */}
                  {existingAttachments.map((attachment, index) => (
                    <div key={`existing-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <Paperclip size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-700">{attachment.original_filename}</span>
                        <span className="text-xs text-gray-500">
                          ({attachment.file_size_mb ? attachment.file_size_mb.toFixed(2) : '0.00'} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExistingAttachment(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  
                  {/* New file uploads */}
                  {formData.attachments.map((file, index) => (
                    <div key={`new-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <Paperclip size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Send size={16} />
              )}
              {isLoading ? 'Sending...' : (editingMeeting ? 'Update Meeting' : 'Schedule & Send Invites')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMeetingModal;
